//? Package Link https://viclafouch.github.io/mui-tel-input/docs/getting-started/
'use client';
import React, { forwardRef, useEffect, useState } from 'react';
import './components.scss';
import {
  MuiTelInput,
  MuiTelInputCountry,
  MuiTelInputInfo,
} from 'mui-tel-input';
import Label from './Label';
import HelperText from './HelperText';
import cn from 'classnames';
import { Controller, useFormContext } from 'react-hook-form';
import { CountryCode } from 'libphonenumber-js';

type Props = {
  id: string;
  status?: 'success' | 'error';
  label?: string | React.ReactNode;
  disabled?: boolean;
  helperText?: string;
  required?: boolean;
  optional?: boolean;
  placeholder?: string;
  defaultCountry?: MuiTelInputCountry;
  labelHelperText?: string;
  onlyCountries?: { code: CountryCode }[];
  dataTestId?: string;
};

const InputMobile = forwardRef<HTMLInputElement, Props>(
  (
    {
      id,
      status,
      label,
      disabled,
      helperText,
      required,
      placeholder,
      optional,
      labelHelperText,
      defaultCountry = 'SA',
      onlyCountries,
      dataTestId,
      ...props
    }: Props,
    ref
  ) => {
    const [number, setNumber] = useState('');

    const methods = useFormContext();
    const phoneNumberFormValue = methods.watch('phoneNumber');

    useEffect(() => {
      if (!phoneNumberFormValue) {
        setNumber('');
        return;
      }
      const code =
        phoneNumberFormValue.code != null
          ? String(phoneNumberFormValue.code).replace(/\+/g, '')
          : '';

      const num =
        phoneNumberFormValue.number != null
          ? String(phoneNumberFormValue.number)
          : '';
      if (code || num) {
        setNumber(code ? `+${code}${num}` : num);
      } else {
        setNumber('');
      }
    }, [
      phoneNumberFormValue,
      phoneNumberFormValue?.code,
      phoneNumberFormValue?.number,
    ]);

    const handleChange = (value: string, info: MuiTelInputInfo) => {
      setNumber(value);
      methods.setValue('phoneNumber.code', `+${info.countryCallingCode}`);
      methods.setValue('phoneNumber.number', info.nationalNumber);
      methods.setValue('phoneNumber.countryCode', info.countryCode);
      methods.trigger('phoneNumber');
    };

    const fillHelperText = methods.formState.errors['phoneNumber']
      ?.message as string;

    return (
      <div className={'inputMobile'}>
        <div
          className={cn('', {
            'inputMobile-success': status === 'success',
            'inputMobile-error': status === 'error',
            disabled: disabled,
          })}
        >
          {label && (
            <Label
              optional={optional}
              labelHelperText={labelHelperText}
              required={required}
              text={label as string}
              htmlFor={id}
            />
          )}
          <Controller
            name="phoneNumber"
            control={methods.control}
            render={({ field }) => {
              return (
                <MuiTelInput
                  {...props}
                  {...field}
                  value={number}
                  onChange={handleChange}
                  disabled={disabled}
                  placeholder={placeholder}
                  SelectProps={{ id: 'country-list-select' }}
                  onlyCountries={onlyCountries?.map((item) => item?.code)}
                  id={id}
                  size="small"
                  ref={ref}
                  defaultCountry={defaultCountry}
                  sx={{
                    '& .MuiInputBase-root': {
                      borderRadius: '12px',
                    },
                  }}
                  //@ts-expect-error type
                  InputProps={{
                    inputProps: {
                      'data-testid': dataTestId,
                    },
                  }}
                />
              );
            }}
          />
          {(helperText || fillHelperText) && (
            <HelperText
              text={helperText || fillHelperText}
              disabled={disabled}
              status={status}
            />
          )}
        </div>
      </div>
    );
  }
);

InputMobile.displayName = 'InputMobile';

export default InputMobile;
